// ===============================================================
// ✅ LoginPage.js — صفحة تسجيل الدخول الرئيسية لتطبيق Livora
// ===============================================================

import React, { useContext, useEffect, useRef, useState } from "react";
import "./Login.css";
import { FaGoogle, FaApple, FaWhatsapp } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLogin } from "../hooks/useLogin";
import { AuthContext } from "../context/AuthContext";
import { checkUserInFirestore } from "../services/checkUserInFirestore";

const APP_NAME = "Livora";
const SPLASH_DURATION_MS = 900;
const SPLASH_FADE_OUT_MS = 400;
const DESKTOP_BREAKPOINT = 769;
const IMAGES_PER_COLUMN = 10;
const UNIQUE_IMAGES = 8;

const COLUMNS = [
  { id: 1, direction: "up" },
  { id: 2, direction: "down" },
  { id: 3, direction: "up" },
];

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`).matches;
  });

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
    const onChange = (e) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return isDesktop;
}

function BackgroundColumns({ prefix }) {
  return (
    <>
      {COLUMNS.map((column) => (
        <div
          key={`${prefix}-col-${column.id}`}
          className={`column column-${column.direction}`}
        >
          <div className="scroll-track">
            {[...Array(IMAGES_PER_COLUMN)].map((_, i) => (
              <div key={i} className="image-container">
                <img
                  src={`/images/login/column${column.id}/${(i % UNIQUE_IMAGES) + 1}.webp`}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                  onError={(e) => {
                    e.target.style.display = "none";
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = "block";
                    }
                  }}
                />
                <div className="image-placeholder" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { user, isReady } = useContext(AuthContext);
  const sessionRestoreStartedRef = useRef(false);

  const [showMain, setShowMain] = useState(false);
  const [splashExiting, setSplashExiting] = useState(false);
  const [showPhoneStep, setShowPhoneStep] = useState(false);
  const [showEmailStep, setShowEmailStep] = useState(false);

  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const {
    isSocialLoginLoading,
    isPhoneSendingCode,
    isEmailLoading,
    handleSocialLogin,
    handlePhoneLogin,
    handleEmailLogin,
  } = useLogin(navigate);

  useEffect(() => {
    if (!isReady || !user || sessionRestoreStartedRef.current) return;

    sessionRestoreStartedRef.current = true;
    void checkUserInFirestore(user, navigate).catch((err) => {
      console.error("LoginPage session restore error:", err);
      sessionRestoreStartedRef.current = false;
    });
  }, [isReady, user, navigate]);

  useEffect(() => {
    const exitTimer = setTimeout(
      () => setSplashExiting(true),
      SPLASH_DURATION_MS - SPLASH_FADE_OUT_MS
    );
    const mainTimer = setTimeout(() => setShowMain(true), SPLASH_DURATION_MS);

    return () => {
      clearTimeout(exitTimer);
      clearTimeout(mainTimer);
      if (typeof window !== "undefined" && window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (_) {}
        delete window.recaptchaVerifier;
      }
    };
  }, []);

  const resetSteps = () => {
    setShowPhoneStep(false);
    setShowEmailStep(false);
    setPhone("");
    setEmail("");
    setPassword("");
  };

  const loginButtons = (
    <div className="login-buttons">
      {!showPhoneStep && !showEmailStep ? (
        <>
          <button
            className="google"
            onClick={() => handleSocialLogin("google")}
            disabled={isSocialLoginLoading}
          >
            <FaGoogle className="icon" />
            {isSocialLoginLoading
              ? t("auth.signing_in", { defaultValue: "Signing in..." })
              : t("auth.google", { defaultValue: "Google" })}
          </button>

          <button
            className="apple"
            onClick={() => handleSocialLogin("apple")}
            disabled={isSocialLoginLoading}
          >
            <FaApple className="icon" /> {t("auth.apple", { defaultValue: "Apple" })}
          </button>

          <button
            className="whatsapp"
            onClick={() => {
              setShowPhoneStep(true);
              setShowEmailStep(false);
            }}
            disabled={isPhoneSendingCode}
          >
            <FaWhatsapp className="icon" />{" "}
            {t("auth.phone_number", { defaultValue: "Phone number" })}
          </button>

          <button
            className="email-login-button"
            onClick={() => {
              setShowEmailStep(true);
              setShowPhoneStep(false);
            }}
            disabled={isEmailLoading}
          >
            {t("auth.email_login", { defaultValue: "Sign in with email" })}
          </button>
        </>
      ) : showPhoneStep ? (
        <>
          <h3>
            {t("auth.phone_login_title", { defaultValue: "Sign in with phone number" })}
          </h3>

          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+43..."
            disabled={isPhoneSendingCode}
          />

          <button onClick={() => handlePhoneLogin(phone)} disabled={isPhoneSendingCode}>
            {isPhoneSendingCode
              ? t("auth.sending_code", { defaultValue: "Sending code..." })
              : t("auth.send_code", { defaultValue: "Send code" })}
          </button>

          <button onClick={resetSteps}>{t("common.back", { defaultValue: "Back" })}</button>
        </>
      ) : (
        <>
          <h3>{t("auth.email_login_title", { defaultValue: "Sign in with email" })}</h3>

          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="example@mail.com"
            disabled={isEmailLoading}
          />

          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("auth.password", { defaultValue: "Password" })}
            disabled={isEmailLoading}
          />

          <button
            onClick={() => handleEmailLogin(email, password)}
            disabled={isEmailLoading}
          >
            {isEmailLoading
              ? t("auth.signing_in", { defaultValue: "Signing in..." })
              : t("auth.sign_in", { defaultValue: "Sign in" })}
          </button>

          <button onClick={resetSteps}>{t("common.back", { defaultValue: "Back" })}</button>
        </>
      )}
    </div>
  );

  if (!showMain) {
    return (
      <div
        className={`livora-splash${splashExiting ? " livora-splash--exit" : ""}`}
        role="status"
        aria-live="polite"
        aria-busy={!splashExiting}
      >
        <div className="livora-splash__orb livora-splash__orb--peach" aria-hidden="true" />
        <div className="livora-splash__orb livora-splash__orb--pink" aria-hidden="true" />
        <div className="livora-splash__orb livora-splash__orb--lavender" aria-hidden="true" />
        <div className="livora-splash__wave livora-splash__wave--one" aria-hidden="true" />
        <div className="livora-splash__wave livora-splash__wave--two" aria-hidden="true" />
        <div className="livora-splash__twinkles" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <div className="livora-splash__inner">
          <div className="livora-splash__mark" aria-hidden="true">
            <span className="livora-splash__mark-petal livora-splash__mark-petal--l" />
            <span className="livora-splash__mark-petal livora-splash__mark-petal--c" />
            <span className="livora-splash__mark-petal livora-splash__mark-petal--r" />
          </div>
          <span className="livora-splash__spark-accent" aria-hidden="true" />
          <h1 className="livora-splash__wordmark">
            <span className="livora-splash__wordmark-text">{APP_NAME}</span>
          </h1>
          <div className="livora-splash__divider" aria-hidden="true">
            <span className="livora-splash__divider-gem" />
          </div>
          <div className="livora-splash__ring" aria-hidden="true" />
          <span className="livora-splash__loading-text">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      {isDesktop ? (
        <div className="desktop-view">
          <div className="image-section">
            <div className="desktop-background">
              <BackgroundColumns prefix="d" />
            </div>
          </div>

          <div className="login-container">
            <h1 className="logo-text desktop-logo">{APP_NAME}</h1>
            {loginButtons}
          </div>
        </div>
      ) : (
        <div className="mobile-view">
          <div className="background">
            <BackgroundColumns prefix="m" />
          </div>

          <div className="overlay">
            <h1 className="logo-text">{APP_NAME}</h1>
            {loginButtons}
            <p className="privacy-notice">
              {t("auth.privacy_notice", {
                defaultValue: "By continuing, you agree to the privacy policy.",
              })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
